/**
 * DÉFRICHAGE — retirer une décoration d'ambiance contre des pièces (ROADMAP 5.3).
 *
 * ══ Ce qu'on retire n'existe pas ═════════════════════════════════════════════
 *
 * ⚠️ Le point à comprendre avant de toucher à ce fichier : **le décor d'ambiance n'est
 * stocké nulle part.** Arbres, buissons, rochers et plantes se DÉRIVENT d'un bruit sur la
 * position de chaque case (`cityRenderer.syncProps`). Il n'y a donc aucun objet à
 * supprimer, aucune liste à amputer.
 *
 * On ne peut que mémoriser ce que le joueur ne veut plus voir. `island.cleared` est donc
 * une liste de MASQUES, pas d'objets — et c'est ce qui la rend robuste : elle ne peut pas
 * se désynchroniser d'une donnée qui n'existe pas.
 *
 * Corollaire pratique : un défrichage est réversible sans rien perdre, et une ville rasée
 * entièrement tient en 144 clés au lieu d'autant d'entités.
 *
 * ══ Pourquoi en pièces, et pourquoi le prix monte ════════════════════════════
 *
 * En pièces parce qu'un arbre mal placé est un problème d'esthétique, jamais de puissance.
 * Le faire payer en gemmes reviendrait à vendre le droit d'avoir une belle ville — la
 * Loi 1 de `GEMMES.md` l'interdit.
 *
 * Le prix monte parce qu'un défrichage gratuit et illimité rendrait la nature sans intérêt :
 * on raserait tout en dix secondes, et la ville perdrait ce qui la rendait vivante.
 * Croissant, chaque retrait devient un choix.
 *
 * ══ Ce que le défrichage ne fait PAS ═════════════════════════════════════════
 *
 * Il ne libère aucune case. Le décor d'ambiance **n'a jamais bloqué la construction** —
 * `syncProps` ne pose ses props que sur ce qui est libre, et un bâtiment posé fait
 * disparaître le décor de lui-même. Défricher est un geste de COMPOSITION, pas de place.
 *
 * C'est important pour l'honnêteté du prix : on ne vend pas une case, on vend une vue.
 */

import { CLEARING } from '@/config/balance';
import type { CitySnapshot } from './gameState';
import { activeIsland } from './gameState';

/** Clé de case, forme unique partagée avec `roads` et le rendu. */
export function clearKey(col: number, row: number): string {
  return `${String(col)}:${String(row)}`;
}

/**
 * Prix du prochain défrichage, en pièces.
 *
 * Même forme que le coût d'un bâtiment — `base × growth^n` — pour que le joueur retrouve
 * une progression qu'il connaît déjà. `n` compte les défrichages de l'ÈRE en cours, pas
 * de toute la partie : le prestige remet la ville à zéro, donc aussi ce compteur.
 */
export function clearCost(alreadyCleared: number): number {
  const count = Math.max(0, Math.floor(alreadyCleared));
  return Math.round(CLEARING.base * Math.pow(CLEARING.growth, count));
}

export type ClearRefusal = 'too-expensive' | 'already-cleared' | 'out-of-grid';

export type ClearResult =
  | { readonly ok: true; readonly snapshot: CitySnapshot; readonly spent: number }
  | { readonly ok: false; readonly refusal: ClearRefusal };

/**
 * Défriche une case. Débite les pièces et mémorise le masque.
 *
 * ⚠️ Aucune vérification qu'un décor s'y trouve RÉELLEMENT, et c'est délibéré : cette
 * information vit dans le rendu (`syncProps`), que `src/core` n'a pas le droit d'importer.
 * L'appelant ne propose le geste que sur une case décorée — c'est lui qui voit l'écran.
 * Refuser ici sur une supposition dupliquerait la règle du bruit dans un module qui ne
 * peut pas la connaître, et les deux copies divergeraient (audit P0.3, même motif).
 */
export function clearCell(
  snapshot: CitySnapshot,
  col: number,
  row: number,
  now: number,
): ClearResult {
  const island = activeIsland(snapshot);

  if (col < 0 || row < 0 || col >= island.grid.cols || row >= island.grid.rows) {
    return { ok: false, refusal: 'out-of-grid' };
  }

  const key = clearKey(col, row);
  if (island.cleared.includes(key)) return { ok: false, refusal: 'already-cleared' };

  const cost = clearCost(island.cleared.length);
  if (snapshot.coins < cost) return { ok: false, refusal: 'too-expensive' };

  return {
    ok: true,
    spent: cost,
    snapshot: {
      ...snapshot,
      coins: snapshot.coins - cost,
      islands: snapshot.islands.map((entry) =>
        entry.id === island.id ? { ...entry, cleared: [...entry.cleared, key] } : entry,
      ),
      updatedAt: now,
    },
  };
}

/**
 * Fait repousser une case défrichée, sans rien rendre.
 *
 * Le remboursement serait la porte ouverte à un aller-retour gratuit ; l'absence de retour
 * en arrière serait une punition pour un geste esthétique. Le compromis : on peut annuler
 * son choix, on ne récupère pas sa mise — exactement comme repeindre un mur.
 *
 * ⚠️ Le compteur de prix, lui, DESCEND : il compte les cases actuellement défrichées, pas
 * les gestes payés. Sinon faire repousser un arbre renchérirait les défrichages suivants
 * sans contrepartie, et le joueur qui se ravise serait puni deux fois.
 */
export function regrowCell(
  snapshot: CitySnapshot,
  col: number,
  row: number,
  now: number,
): CitySnapshot {
  const island = activeIsland(snapshot);
  const key = clearKey(col, row);
  if (!island.cleared.includes(key)) return snapshot;

  return {
    ...snapshot,
    islands: snapshot.islands.map((entry) =>
      entry.id === island.id
        ? { ...entry, cleared: entry.cleared.filter((entryKey) => entryKey !== key) }
        : entry,
    ),
    updatedAt: now,
  };
}
